/**
 * What was loaded and how well it went. One row per import; open a row to see
 * which source column ended up in which field.
 *
 * Een rij is ook een keuze: het vinkje vooraan zet die import aan of uit in het
 * filter, en omdat het filter in de URL staat toont de kaart daarna precies
 * dezelfde woningen als de tabel eronder. Meerdere tegelijk mag - twee scrapes
 * naast elkaar zijn pas te vergelijken als je ze los kunt aanzetten.
 */
import { type Dataset, type FotoTelling, formatAantal, formatDatumTijd } from '@mamimap/shared';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Bevestiging } from '@/components/Bevestiging';
import { useVerwijderDataset } from '@/lib/api/queries';
import type { FilterState } from '@/lib/filter-url';
import { woningenTekst } from './selectie';

export function DatasetTabel({
  datasets,
  state,
}: {
  datasets: readonly Dataset[];
  state: FilterState;
}) {
  const [open, zetOpen] = useState<string | null>(null);
  /* De dataset waarvoor de bevestiging open staat; één tegelijk. */
  const [teVerwijderen, zetTeVerwijderen] = useState<string | null>(null);
  const [verwijderd, zetVerwijderd] = useState<string | null>(null);
  const verwijderen = useVerwijderDataset();
  const alleenZonder = state.filter.zonderCoordinaat === true;
  const gekozen = state.filter.datasets ?? [];

  function wissel(id: string) {
    state.zet({
      datasets: gekozen.includes(id)
        ? gekozen.filter((bestaand) => bestaand !== id)
        : [...gekozen, id],
    });
  }

  return (
    <div className="overflow-x-auto border border-line bg-surface-strong">
      <table className="tabel">
        <thead>
          <tr>
            <th className="w-7">
              <span className="sr-only">In selectie</span>
            </th>
            <th className="w-6" />
            <th>Bron</th>
            <th>Bestand</th>
            <th>Geïmporteerd</th>
            <th className="text-right">Rijen</th>
            <th className="text-right">Dubbel</th>
            <th className="text-right">Woningen</th>
            <th className="text-right">Gegeocodeerd</th>
            <th className="text-right">Zonder punt</th>
            <th className="text-right">Met label</th>
            <th className="text-right">Foto’s</th>
            <th>Niet herkend</th>
            <th className="w-7">
              <span className="sr-only">Verwijderen</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {datasets.map((dataset) => {
            const uitgeklapt = open === dataset.id;
            const aan = gekozen.includes(dataset.id);
            return (
              <Fragment key={dataset.id}>
                <tr data-gekozen={aan}>
                  {/* Het vinkje filtert; de bestandsnaam verderop is zijn label,
                      zodat een klik op de naam hetzelfde doet. */}
                  <td className="pr-0">
                    <input
                      type="checkbox"
                      id={`dataset-${dataset.id}`}
                      checked={aan}
                      onChange={() => wissel(dataset.id)}
                      className="h-3.5 w-3.5 accent-accent align-middle"
                    />
                  </td>
                  <td className="pr-0">
                    <button
                      type="button"
                      onClick={() => zetOpen(uitgeklapt ? null : dataset.id)}
                      aria-expanded={uitgeklapt}
                      aria-label={`Kolommen van ${dataset.bestandsnaam}`}
                      className="text-faint hover:text-ink"
                    >
                      {uitgeklapt ? (
                        <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
                      )}
                    </button>
                  </td>
                  <td className="text-ink">{dataset.bron}</td>
                  <td>
                    <label htmlFor={`dataset-${dataset.id}`} className="cursor-pointer">
                      {dataset.bestandsnaam}
                    </label>
                  </td>
                  <td className="cijfers">{formatDatumTijd(dataset.geimporteerdOp)}</td>
                  <td className="cijfers text-right">{formatAantal(dataset.aantalRijen)}</td>
                  <td className="cijfers text-right text-muted">
                    {dataset.aantalDubbel === 0 ? '–' : formatAantal(dataset.aantalDubbel)}
                  </td>
                  <td className="cijfers text-right text-ink">
                    {formatAantal(dataset.aantalWoningen)}
                  </td>
                  <td className="cijfers text-right">
                    {formatAantal(dataset.aantalGegeocodeerd)}
                    <span className="pl-1.5 text-faint">
                      {deel(dataset.aantalGegeocodeerd, dataset.aantalWoningen)}
                    </span>
                  </td>
                  {/* Wat er niet op de kaart komt, hoort naast wat er wel op komt:
                      anders moet de lezer 928 - 894 zelf uitrekenen. De cel is een
                      knop, want daarna wil je weten wélke. */}
                  <td className="cijfers text-right">
                    {zonderPunt(dataset) === 0 ? (
                      <span className="text-faint">–</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() =>
                          state.zet({ zonderCoordinaat: alleenZonder ? undefined : true })
                        }
                        aria-pressed={alleenZonder}
                        title="Toon deze woningen in de tabel hieronder"
                        className={`underline-offset-2 hover:underline ${
                          alleenZonder ? 'text-accent' : 'text-ink-soft hover:text-ink'
                        }`}
                      >
                        {formatAantal(zonderPunt(dataset))}
                      </button>
                    )}
                  </td>
                  <td className="cijfers text-right">
                    {formatAantal(dataset.aantalMetLabel)}
                    <span className="pl-1.5 text-faint">
                      {deel(dataset.aantalMetLabel, dataset.aantalWoningen)}
                    </span>
                  </td>
                  {/* Wat er op schijf staat, en wat de fotohaler nog moet doen.
                      Opgegeven foto's staan erbij in de kleur van een fout,
                      want dat zijn ze: drie keer geprobeerd, drie keer mis. */}
                  <td className="cijfers text-right" title={fotoTitel(dataset.fotos)}>
                    {dataset.fotos.bewaard === 0 && dataset.fotos.teHalen === 0 ? (
                      <span className="text-faint">–</span>
                    ) : (
                      <>
                        {formatAantal(dataset.fotos.bewaard)}
                        {dataset.fotos.teHalen > 0 ? (
                          <span className="pl-1.5 text-faint">
                            +{formatAantal(dataset.fotos.teHalen)}
                          </span>
                        ) : null}
                        {dataset.fotos.mislukt > 0 ? (
                          <span className="pl-1.5 text-brick">
                            {formatAantal(dataset.fotos.mislukt)} mis
                          </span>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td className="max-w-64 truncate text-muted">
                    {dataset.nietHerkendeKolommen.length === 0
                      ? '–'
                      : dataset.nietHerkendeKolommen.join(', ')}
                  </td>
                  <td className="pr-1">
                    <button
                      type="button"
                      onClick={() =>
                        zetTeVerwijderen(teVerwijderen === dataset.id ? null : dataset.id)
                      }
                      aria-expanded={teVerwijderen === dataset.id}
                      aria-label={`${dataset.bestandsnaam} verwijderen`}
                      className="text-faint hover:text-brick"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
                {teVerwijderen === dataset.id ? (
                  <tr>
                    <td colSpan={14} className="bg-surface p-2">
                      <Bevestiging
                        vraag={`${woningenTekst(dataset.aantalWoningen)} verwijderen uit ${dataset.bestandsnaam}? Dit is niet terug te draaien.`}
                        uitleg="Woningen die intussen door een andere import of een zoekopdracht zijn overgenomen blijven staan. Een woning die nog te koop staat komt bij de volgende ronde van de extensie weer binnen; afkeuren is dan de weg."
                        knop="Verwijderen"
                        bezig={verwijderen.isPending}
                        fout={verwijderen.error}
                        onBevestig={() =>
                          verwijderen.mutate(dataset.id, {
                            onSuccess: (uit) => {
                              zetTeVerwijderen(null);
                              zetVerwijderd(
                                `${uit.bestandsnaam}: ${woningenTekst(uit.woningen)} verwijderd${
                                  uit.elders > 0
                                    ? `; ${woningenTekst(uit.elders)} uit dit bestand ${
                                        uit.elders === 1 ? 'hoorde' : 'hoorden'
                                      } al bij een andere dataset of ${uit.elders === 1 ? 'was' : 'waren'} al weg`
                                    : ''
                                }.`,
                              );
                            },
                          })
                        }
                        onAnnuleer={() => zetTeVerwijderen(null)}
                      />
                    </td>
                  </tr>
                ) : null}
                {uitgeklapt ? (
                  <tr>
                    <td colSpan={14} className="bg-surface">
                      <div className="grid grid-cols-2 gap-x-6 gap-y-[2px] py-1.5 sm:grid-cols-3 lg:grid-cols-4">
                        {dataset.herkendeKolommen.map((kolom) => (
                          // Eén bronkolom kan twee velden vullen - zonder kopregel
                          // levert de URL-kolom zowel bronId als bronUrl - dus is
                          // de kolomnaam alleen geen unieke sleutel.
                          <div
                            key={`${kolom.kolom}→${kolom.veld}`}
                            className="flex gap-1.5 text-[11.5px]"
                          >
                            <span className="text-muted">{kolom.kolom}</span>
                            <span className="text-faint">→</span>
                            <span className="text-ink">{kolom.veld}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {verwijderd ? (
        <p className="border-t border-line px-3 py-2 text-[12px] text-muted">{verwijderd}</p>
      ) : null}
    </div>
  );
}

/** Woningen van deze dataset die nu geen coördinaat hebben. */
function zonderPunt(dataset: Dataset): number {
  return Math.max(0, dataset.aantalWoningen - dataset.aantalGegeocodeerd);
}

function deel(aantal: number, totaal: number): string {
  if (totaal === 0) return '';
  return `${Math.round((aantal / totaal) * 100)}%`;
}

/** De titel bij de fotokolom: de drie getallen uitgeschreven, plus de ruimte op schijf. */
function fotoTitel(fotos: FotoTelling): string {
  const delen = [`${formatAantal(fotos.bewaard)} op schijf`];
  if (fotos.teHalen > 0) delen.push(`${formatAantal(fotos.teHalen)} nog te halen`);
  if (fotos.mislukt > 0) delen.push(`${formatAantal(fotos.mislukt)} opgegeven na drie pogingen`);
  if (fotos.bytes > 0) delen.push(formatBytes(fotos.bytes));
  return delen.join(', ');
}

/** `1,4 GB`, `312 MB`, `80 kB` - voor op het scherm, Nederlandse notatie. */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3)
    return `${(bytes / 1024 ** 3).toLocaleString('nl-NL', { maximumFractionDigits: 1 })} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2).toLocaleString('nl-NL')} MB`;
  return `${Math.round(bytes / 1024).toLocaleString('nl-NL')} kB`;
}
